/**
 * One chat row — dispatches by `kind` (Phase 17). Text messages
 * render as bubbles; `quote` / `quote_superseded` / `expired_quote` /
 * `approval` render via `QuoteCard`; `intent_picker` renders via
 * `IntentPicker`.
 *
 * Pre-Phase-17 messages default to `kind="text"` so legacy bubbles
 * keep their existing behaviour.
 */

import type {
  ChatMessage as ChatMessageRow,
  Estimate,
} from "@starz/shared/api/types.gen";

import type { Photo } from "@starz/shared/api/types.gen";

import { AddPhotoButton } from "./AddPhotoButton";
import { IntentPicker } from "./IntentPicker";
import { QuoteCard } from "./QuoteCard";
import { RenderCard } from "./RenderCard";

interface Props {
  message: Pick<
    ChatMessageRow,
    | "role"
    | "content"
    | "truncated"
    | "is_clarification"
    | "kind"
    | "quote_payload"
    | "options_payload"
    | "render_payload"
    | "photo_request"
  >;
  /** Live Estimate row — quote cards read `current_offer` from here so
   * the countdown stays in sync with admin edits. */
  estimate?: Estimate | null;
  onPayDeposit?: (estimateId: string) => void;
  onRefreshed?: () => void;
  onSelectIntent?: (intentId: string, displayName: string) => void;
  intentPickerDisabled?: boolean;
  // Phase 18 — render lifecycle callbacks. Optional; only the customer
  // workspace wires them up.
  onApproveRender?: (renderId: string) => void;
  onRejectRender?: (
    renderId: string,
    feedback?: { reason?: string; category?: import("@starz/shared/api/types.gen").RejectionCategory },
  ) => void;
  onOpenLightbox?: (url: string, alt: string) => void;
  renderBusy?: boolean;
  /** "Continue your Project" CTA on an approved render. */
  onContinueProject?: () => void;
  // Phase 18 — `[[ADD_PHOTO]]` upload button wiring.
  estimateId?: string;
  onPhotoUploaded?: (photo: Photo) => void;
}

export function ChatMessage({
  message,
  estimate,
  onPayDeposit,
  onRefreshed,
  onSelectIntent,
  intentPickerDisabled,
  onApproveRender,
  onRejectRender,
  onOpenLightbox,
  renderBusy,
  onContinueProject,
  estimateId,
  onPhotoUploaded,
}: Props) {
  const kind = message.kind ?? "text";

  if (kind === "intent_picker") {
    return (
      <IntentPicker
        message={message}
        onSelectIntent={(id, name) => onSelectIntent?.(id, name)}
        disabled={intentPickerDisabled}
      />
    );
  }

  if (
    kind === "quote" ||
    kind === "quote_superseded" ||
    kind === "expired_quote" ||
    kind === "approval"
  ) {
    return (
      <QuoteCard
        message={message}
        estimate={estimate}
        onPayDeposit={onPayDeposit}
        onRefreshed={onRefreshed}
      />
    );
  }

  if (
    kind === "render_generating" ||
    kind === "render_complete" ||
    kind === "render_approved" ||
    kind === "render_rejected"
  ) {
    return (
      <RenderCard
        message={message}
        onApprove={onApproveRender}
        onReject={onRejectRender}
        onOpenLightbox={onOpenLightbox}
        onContinue={onContinueProject}
        busy={renderBusy}
      />
    );
  }

  // Default — legacy text bubble.
  const isUser = message.role === "user";
  const isClarification = !isUser && message.is_clarification === true;
  const bubbleClass = isUser
    ? "max-w-prose rounded-2xl rounded-br-md bg-starz-navy px-5 py-3 text-base text-starz-off-white"
    : isClarification
      ? "max-w-prose rounded-2xl rounded-bl-md border-2 border-violet-300 bg-violet-50 px-5 py-3 text-base text-starz-charcoal"
      : "max-w-prose rounded-2xl rounded-bl-md border border-starz-gold/20 bg-starz-cream px-5 py-3 text-base text-starz-charcoal";
  return (
    <div className={isUser ? "flex justify-end" : "flex flex-col items-start"}>
      <div className={bubbleClass}>
        {isClarification && (
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-violet-900">
            Question from your contractor
          </p>
        )}
        <p className="whitespace-pre-wrap">{message.content}</p>
        {message.truncated && !isUser && (
          <p className="mt-2 text-xs italic text-warm-gray">
            (response cut off — try again or rephrase)
          </p>
        )}
      </div>
      {/* Phase 18 — inline upload button when Stella emitted [[ADD_PHOTO]]. */}
      {!isUser && message.photo_request && estimateId && (
        <AddPhotoButton estimateId={estimateId} onUploaded={onPhotoUploaded} />
      )}
    </div>
  );
}
